import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { addMonths, isBefore, parseISO } from 'date-fns'
import {
  catchError,
  debounceTime,
  filter,
  of,
  startWith,
  switchMap,
} from 'rxjs'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import { AuthService } from '../../../services/auth/auth.service'
import { getArticleIdFromRoute } from '../../auth/util/article-purchase-route.utils'
import {
  ArticlePurchaseFlowContextComponent,
  ArticlePurchaseFlowStep,
} from './article-purchase-flow-context.component'

interface ArticlePurchaseStepperState {
  currentStep: ArticlePurchaseFlowStep
  includeStudentEmailStep: boolean
  includeVerificationStep: boolean
}

@Component({
  selector: 'dhp-article-purchase-shell',
  templateUrl: './article-purchase-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, TranslatePipe, ArticlePurchaseFlowContextComponent],
})
export class ArticlePurchaseShellComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly articlesService = inject(ArticlesService)
  private readonly authService = inject(AuthService)

  readonly currentStep = signal<ArticlePurchaseFlowStep | null>(null)
  readonly includeStudentEmailStep = signal(false)
  readonly includeVerificationStep = signal(true)

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        debounceTime(0),
        switchMap(() => this.resolveStepperState()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(stepperState => {
        if (!stepperState) {
          return
        }

        this.currentStep.set(stepperState.currentStep)
        this.includeStudentEmailStep.set(stepperState.includeStudentEmailStep)
        this.includeVerificationStep.set(stepperState.includeVerificationStep)
      })
  }

  private resolveStepperState() {
    const activeRouteSnapshot = this.getActiveRouteSnapshot()

    if (
      activeRouteSnapshot === this.route.snapshot &&
      this.route.snapshot.firstChild === null &&
      this.route.snapshot.data['purchaseStep'] === undefined
    ) {
      return of(null)
    }

    const purchaseStep: unknown = activeRouteSnapshot.data['purchaseStep']
    const includeVerificationStep =
      this.shouldIncludeVerificationStep(activeRouteSnapshot)
    const currentStep: ArticlePurchaseFlowStep =
      purchaseStep === 'verify' ||
      purchaseStep === 'checkout' ||
      purchaseStep === 'continueEmail' ||
      purchaseStep === 'success'
        ? purchaseStep
        : 'account'

    if (currentStep === 'continueEmail') {
      return of({
        currentStep,
        includeStudentEmailStep: true,
        includeVerificationStep,
      } satisfies ArticlePurchaseStepperState)
    }

    if (currentStep === 'checkout') {
      return this.resolveCheckoutStepperState(
        currentStep,
        includeVerificationStep
      )
    }

    if (currentStep !== 'success') {
      return of({
        currentStep,
        includeStudentEmailStep: false,
        includeVerificationStep,
      } satisfies ArticlePurchaseStepperState)
    }

    const purchaseId = this.parseRouteNumber(
      activeRouteSnapshot.queryParamMap.get('purchaseId')
    )

    if (purchaseId === null) {
      return of({
        currentStep,
        includeStudentEmailStep: false,
        includeVerificationStep,
      } satisfies ArticlePurchaseStepperState)
    }

    return this.articlesService.getUserArticlePurchaseById(purchaseId).pipe(
      switchMap(purchase => {
        const articleId = purchase?.article?.id

        if (typeof articleId !== 'number') {
          return of(false)
        }

        return this.articlesService
          .getArticleDetails(articleId, 'cache-first')
          .pipe(
            switchMap(article =>
              of(
                this.didUseStudentEmailContinuation(
                  purchase?.purchase_price,
                  article?.purchase_price?.studentAmount
                )
              )
            ),
            catchError(() => of(false))
          )
      }),
      switchMap(includeStudentEmailStep =>
        of({
          currentStep,
          includeStudentEmailStep,
          includeVerificationStep,
        } satisfies ArticlePurchaseStepperState)
      ),
      catchError(() =>
        of({
          currentStep,
          includeStudentEmailStep: false,
          includeVerificationStep,
        } satisfies ArticlePurchaseStepperState)
      )
    )
  }

  private getActiveRouteSnapshot(): ActivatedRouteSnapshot {
    let activeRouteSnapshot = this.route.snapshot

    while (activeRouteSnapshot.firstChild) {
      activeRouteSnapshot = activeRouteSnapshot.firstChild
    }

    return activeRouteSnapshot
  }

  private resolveCheckoutStepperState(
    currentStep: ArticlePurchaseFlowStep,
    includeVerificationStep: boolean
  ) {
    const articleId = getArticleIdFromRoute(this.route)

    if (articleId === null || !this.isStudentEmailVerificationExpired()) {
      return of({
        currentStep,
        includeStudentEmailStep: false,
        includeVerificationStep,
      } satisfies ArticlePurchaseStepperState)
    }

    return this.articlesService
      .getArticleDetails(articleId, 'cache-first')
      .pipe(
        switchMap(article =>
          of({
            currentStep,
            includeStudentEmailStep:
              article?.purchase_price?.studentPricingApplies === true,
            includeVerificationStep,
          } satisfies ArticlePurchaseStepperState)
        ),
        catchError(() =>
          of({
            currentStep,
            includeStudentEmailStep: false,
            includeVerificationStep,
          } satisfies ArticlePurchaseStepperState)
        )
      )
  }

  private shouldIncludeVerificationStep(
    activeRouteSnapshot: ActivatedRouteSnapshot
  ): boolean {
    const routeDataValue: unknown =
      activeRouteSnapshot.data['includeVerificationStep']

    if (typeof routeDataValue === 'boolean') {
      return routeDataValue
    }

    return activeRouteSnapshot.queryParamMap.get('accountFlow') !== 'update'
  }

  private didUseStudentEmailContinuation(
    purchasePrice: number | null | undefined,
    studentAmount: number | null | undefined
  ): boolean {
    if (
      typeof purchasePrice !== 'number' ||
      typeof studentAmount !== 'number' ||
      !this.isStudentEmailVerificationExpired()
    ) {
      return false
    }

    return purchasePrice === studentAmount
  }

  /**
   * Whether this account's e-mail verification is too old to carry a student
   * purchase. This answers age only -- it deliberately says nothing about
   * whether the visitor is a student, because that is the server's call and
   * arrives on `purchase_price.studentPricingApplies`. Mixing the two here
   * would let a stale client-side `isStudent` hide the continue-in-email step
   * from someone the checkout is about to route through it. Matches
   * `ArticlePurchaseCheckoutComponent.isStudentEmailVerificationExpired`.
   */
  private isStudentEmailVerificationExpired(): boolean {
    const emailVerifiedAt = this.authService.currentUser()?.emailVerifiedAt

    if (!emailVerifiedAt) {
      return true
    }

    return isBefore(parseISO(emailVerifiedAt), addMonths(new Date(), -6))
  }

  private parseRouteNumber(value: string | null): number | null {
    if (!value) {
      return null
    }

    const parsedValue = parseInt(value, 10)
    return isNaN(parsedValue) ? null : parsedValue
  }
}
